import {
  AT_MIN_ZOOM_EPSILON,
  resolveContinuousFitZoomUpdate,
} from 'features/knowledgeDocumentViewer/components/viewer/continuous/hooks/resolveContinuousFitZoomUpdate'
import {
  type ContinuousZoomAnchor,
  createContinuousZoomAnchor,
  resolveContinuousZoomScroll,
} from 'features/knowledgeDocumentViewer/components/viewer/continuous/resolveContinuousZoomScroll'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

export const MAX_ZOOM = 3

export function useContinuousZoom(
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
  pageDimensions: { w: number; h: number } | null,
  containerSize: { w: number; h: number },
  scrollPadding: number,
  pageGap: number,
  totalPages: number,
  getPageHeightForZoom: (zoom: number, page: number) => number,
  initialZoom = 0
) {
  const [zoom, setZoom] = useState(initialZoom)
  const [minZoom, setMinZoom] = useState(0)
  const zoomAnchorRef = useRef<ContinuousZoomAnchor | null>(null)
  const effectiveZoomRef = useRef(0)
  const minZoomRef = useRef(0)
  const containerWidthRef = useRef(containerSize.w)
  const containerHeightRef = useRef(containerSize.h)
  const getPageHeightForZoomRef = useRef(getPageHeightForZoom)
  getPageHeightForZoomRef.current = getPageHeightForZoom

  const calculateMinZoom = useCallback(() => {
    if (!pageDimensions || containerSize.w === 0 || containerSize.h === 0) {
      return 0
    }
    const padding = scrollPadding * 2
    const scaleX = (containerSize.w - padding) / pageDimensions.w
    const scaleY = (containerSize.h - padding) / pageDimensions.h
    return Math.min(scaleX, scaleY)
  }, [pageDimensions, containerSize, scrollPadding])

  const effectiveZoom = useMemo(() => Math.max(zoom, minZoom), [zoom, minZoom])
  effectiveZoomRef.current = effectiveZoom

  const snapshotZoomAnchor = useCallback(
    (focal?: { x: number; y: number }) => {
      const container = scrollContainerRef.current
      if (!container || effectiveZoomRef.current === 0) return null
      return createContinuousZoomAnchor(
        container,
        effectiveZoomRef.current,
        focal
      )
    },
    [scrollContainerRef]
  )

  const captureAnchor = useCallback(() => {
    const anchor = snapshotZoomAnchor()
    if (anchor) zoomAnchorRef.current = anchor
  }, [snapshotZoomAnchor])

  const applyAnchoredZoom = useCallback(
    (level: number, anchor: ContinuousZoomAnchor) => {
      const nextZoom = Math.min(Math.max(level, minZoomRef.current), MAX_ZOOM)
      // Only retain the anchor when effectiveZoom will actually change;
      // otherwise the layout effect won't run and the stale anchor would
      // jump scroll on a future resize/rotation.
      if (Math.abs(nextZoom - effectiveZoomRef.current) < 0.0001) {
        zoomAnchorRef.current = null
        return
      }
      zoomAnchorRef.current = anchor
      setZoom(nextZoom)
    },
    []
  )

  useEffect(() => {
    const decision = resolveContinuousFitZoomUpdate({
      newMin: calculateMinZoom(),
      previousMin: minZoomRef.current,
      previousZoom: effectiveZoomRef.current,
      prevWidth: containerWidthRef.current,
      containerWidth: containerSize.w,
      containerHeight: containerSize.h,
    })
    if (decision.kind === 'ignore') return

    minZoomRef.current = decision.nextMinZoom
    setMinZoom(decision.nextMinZoom)
    containerWidthRef.current = decision.nextWidth
    containerHeightRef.current = decision.nextHeight
    if (decision.nextZoom !== null) {
      setZoom(decision.nextZoom)
    }
  }, [calculateMinZoom, containerSize])

  useLayoutEffect(() => {
    const anchor = zoomAnchorRef.current
    const container = scrollContainerRef.current
    if (!anchor || !container || effectiveZoom === 0) return

    const next = resolveContinuousZoomScroll({
      anchor,
      newZoom: effectiveZoom,
      scrollPadding,
      totalPages,
      pageGap,
      getPageHeightAtNewZoom: (page) =>
        getPageHeightForZoomRef.current(effectiveZoom, page),
    })
    container.scrollTop = next.scrollTop
    container.scrollLeft = next.scrollLeft
    zoomAnchorRef.current = null
  }, [effectiveZoom, pageGap, scrollContainerRef, scrollPadding, totalPages])

  const zoomIn = useCallback(() => {
    captureAnchor()
    setZoom((prev) => Math.min(prev * 1.5, MAX_ZOOM))
  }, [captureAnchor])

  const zoomOut = useCallback(() => {
    captureAnchor()
    setZoom((prev) => Math.max(prev / 1.5, minZoom))
  }, [minZoom, captureAnchor])

  const resetZoom = useCallback(() => {
    captureAnchor()
    setZoom(minZoom)
  }, [minZoom, captureAnchor])

  const setZoomLevel = useCallback(
    (level: number) => {
      setZoom(Math.min(Math.max(level, minZoom), MAX_ZOOM))
    },
    [minZoom]
  )

  const isAtMinZoom =
    minZoom > 0 && Math.abs(effectiveZoom - minZoom) / minZoom < 0.02
  const isAtMaxZoom = effectiveZoom >= MAX_ZOOM - AT_MIN_ZOOM_EPSILON

  return {
    effectiveZoom,
    minZoom,
    zoomIn,
    zoomOut,
    resetZoom,
    setZoomLevel,
    snapshotZoomAnchor,
    applyAnchoredZoom,
    isAtMinZoom,
    isAtMaxZoom,
  }
}
